import { ChevronRight } from "lucide-react"
import { get } from "radash"

import type { HistoryRide } from "@be-miniapp/api"
import { TZ } from "@be-miniapp/app-configs"
import { useTranslation } from "@be-miniapp/i18n"
import { dayjs } from "@be-miniapp/shared/utils/date"
import { convertAppDeeplinkToWebUrl } from "@be-miniapp/shared/utils/deeplink"
import { formatCurrency } from "@be-miniapp/shared/utils/number"
import { Button } from "@be-miniapp/ui/components/ui/button"
import { Skeleton } from "@be-miniapp/ui/components/ui/skeleton"
import { Typography } from "@be-miniapp/ui/components/ui/typography"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import { useRideDataStore } from "@/store/ride-data"

interface TransportActivityItemProps {
  item: HistoryRide
}

const TransportActivityItem = ({ item }: TransportActivityItemProps) => {
  const navigate = useNavigate()

  const { t } = useTranslation()

  const rebookDeeplink = get(item, "rebook_deeplink", null) as string | null
  const returnDeeplink = get(item, "reverse_book_deeplink", null) as
    | string
    | null

  const setPickup = useRideDataStore((state) => state.setPickup)
  const setDropoff = useRideDataStore((state) => state.setDropoff)

  const engagementId = get(item, "engagement_id", 0)

  return (
    <div
      onClick={() =>
        navigate(
          `/activities/transport/detail/${engagementId}/${get(item, "session_id", "")}`,
          {
            animate: true,
          }
        )
      }
      className="flex w-full flex-col gap-2.5 bg-white px-4 py-3"
    >
      <div className="flex justify-between">
        <span className="font-medium text-[0.72rem] text-gray-500">
          {dayjs(item.accept_time || item.created_at, "YYYY-MM-DD HH:mm:ss")
            .tz(TZ)
            .format("HH:mm, DD/MM")}
        </span>

        {item.is_cancelled_ride ? (
          <div>
            <Typography.Caption
              className="rounded-lg bg-gray-200/70 px-1 py-0.5 font-medium text-gray-600 text-xs leading-5"
              variant="semi"
            >
              {t("common.cancelled")}
            </Typography.Caption>
          </div>
        ) : (
          <div>
            <Typography.Caption
              className="rounded-lg bg-green-200/70 px-1 py-0.5 font-medium text-green-800 leading-5"
              variant="semi"
            >
              {t("common.completed")}
            </Typography.Caption>
          </div>
        )}
      </div>
      <div className="flex justify-between">
        <div className="flex w-full items-center gap-2 overflow-hidden">
          <img
            src={item.images.invoice_icon}
            className="h-auto w-11 object-cover"
            alt={item.vehicle_name}
          />

          <div className="flex flex-col items-center gap-0.5">
            <div className="size-3 rounded-full bg-be-blue"></div>
            <div className="size-[0.2rem] rounded-full bg-gray-300"></div>
            <div className="size-[0.2rem] rounded-full bg-gray-300"></div>
            <div className="size-3 rounded-full bg-be-yellow"></div>
          </div>

          <div className="flex w-full flex-1 shrink flex-col gap-1">
            <span className="line-clamp-1 text-gray-800 text-sm">
              {item.pickup_address}
            </span>
            <span className="line-clamp-1 text-gray-800 text-sm">
              {item.drop_address}
            </span>
          </div>
          <div className="flex shrink-0 self-start">
            <span className="font-medium">
              {formatCurrency(item.amount, item.currency)}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-1 flex items-center justify-between">
        <div className="flex items-center text-gray-600 text-xs">
          {t("detail")} <ChevronRight size={12} className="shrink-0" />
        </div>

        <div className="flex gap-2">
          {returnDeeplink && (
            <Button
              className="h-5 bg-gray-300/70 px-1.5 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                setPickup(undefined)
                setDropoff(undefined)
                navigate(convertAppDeeplinkToWebUrl(returnDeeplink))
              }}
            >
              {t("transport_return_book")}
            </Button>
          )}
          {rebookDeeplink && (
            <Button
              className="h-5 px-1.5 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                setPickup(undefined)
                setDropoff(undefined)
                navigate(convertAppDeeplinkToWebUrl(rebookDeeplink))
              }}
            >
              {t("transport_rebook")}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

TransportActivityItem.displayName = "TransportActivityItem"

const TransportActivityItemSkeleton = () => {
  return (
    <div className="flex w-full flex-col gap-2.5 bg-white px-4 py-3">
      {/* Header row - time and status */}
      <div className="flex justify-between">
        <Skeleton className="h-3 w-20 bg-gray-300" />
        <Skeleton className="h-5 w-16 rounded-lg bg-gray-200" />
      </div>

      {/* Main content row */}
      <div className="flex justify-between">
        <div className="flex w-full items-center gap-2 overflow-hidden">
          {/* Vehicle image */}
          <Skeleton className="h-11 w-11 bg-gray-300" />

          {/* Route dots */}
          <div className="flex flex-col items-center gap-0.5">
            <div className="size-3 rounded-full bg-gray-300"></div>
            <div className="size-[0.2rem] rounded-full bg-gray-300"></div>
            <div className="size-[0.2rem] rounded-full bg-gray-300"></div>
            <div className="size-3 rounded-full bg-gray-300"></div>
          </div>

          {/* Addresses */}
          <div className="flex w-full flex-1 shrink flex-col gap-1">
            <Skeleton className="h-3.5 w-40 bg-gray-300" />
            <Skeleton className="h-3.5 w-48 bg-gray-300" />
          </div>

          {/* Price */}
          <div className="flex shrink-0 self-start">
            <Skeleton className="h-4 w-16 bg-gray-300" />
          </div>
        </div>
      </div>

      {/* Bottom row - detail link and buttons */}
      <div className="mt-1 flex items-center justify-between">
        <Skeleton className="h-3 w-12 bg-gray-200" />
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16 bg-gray-200" />
          <Skeleton className="h-5 w-14 bg-gray-300" />
        </div>
      </div>
    </div>
  )
}

TransportActivityItemSkeleton.displayName = "TransportActivityItemSkeleton"

TransportActivityItem.Skeleton = TransportActivityItemSkeleton

export { TransportActivityItem }
